/**
 * Context Packet Inspector (S13 / P2 #22 — the "what was redacted / truncated /
 * stale" half of the inspectability promise, screens/01-context-explorer drill).
 *
 * Renders one rendered context packet (the ViewPacket the Explorer/Lens runtime
 * produces) and makes its three architecture-level constraints EXPLICIT:
 *
 *   • redacted  — fields/nodes withheld by access policy (count + reason; the
 *                 withheld values are never rendered);
 *   • overflow  — nodes/edges hidden past the budget (count + reason);
 *   • stale     — the packet's source watermark vs. now (a freshness signal).
 *
 * Doctrine (design-system §"State Requirements"): redaction and overflow are
 * architecture constraints, not polish — a packet that silently drops withheld
 * or truncated data is wrong. This screen surfaces them through the shared
 * RenderState markers so every screen reads them the same way. It is a READ
 * projection: it renders the packet a server already authorized and never
 * exposes credentials, raw secrets, or sealed names.
 *
 * Backing read: the packet is rendered server-side by the Explorer runtime
 * (`GET /contexts/:id/explorer?lens_id=…`, which already exists). A lens id is
 * required to render; without one the screen shows a truthful empty/proposed
 * state rather than a blank graph.
 *
 * Local packet type: the ViewPacket schema is not re-exported from `@aos/shared`
 * (it lives in the worker's view-runtime schema), so the minimal wire shape this
 * screen reads is declared locally rather than reaching into a non-owned barrel.
 */
import * as React from "react";
import { useParams, useSearchParams } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Icon } from "@/components/ui/icon";
import {
  OverflowBanner,
  RedactedBanner,
  RenderState,
  StaleBanner,
} from "@/components/app/RenderState";
import { StatusBadge } from "@/components/app/StatusBadge";
import { PageHeader } from "@/components/layout/PageHeader";
import { Field, FieldGrid, InspectorSection } from "@/components/inspector";
import { ApiError, api } from "@/lib/api";
import { formatDateTime, formatRelative, humanize } from "@/lib/format";

/** Minimal ViewPacket wire shape this inspector reads (view_packet/v3 subset). */
interface ContextPacket {
  view_packet_version: string;
  meta: {
    root_context_ids: string[];
    lens_id: string;
    lens_version: string;
    semantic_level: string;
    node_count: number;
    edge_count: number;
    truncated: boolean;
    budget: { max_nodes: number; max_edges: number };
    generated_at: string;
    source_watermark: string;
  };
  nodes: Array<{
    id: string;
    kind: string;
    label: string;
    status: { kind: string; label: string };
  }>;
  redactions: Array<{
    scope: "node" | "edge" | "panel" | "action";
    reason: string;
    disclosure: "none" | "existence" | "aggregate";
  }>;
  overflow: {
    hidden_node_count: number;
    hidden_edge_count: number;
    reason: string | null;
  };
}

/** A packet is stale when its source watermark is older than the budget (15m). */
const STALE_BUDGET_MS = 15 * 60 * 1000;

function isStale(packet: ContextPacket): boolean {
  const at = new Date(packet.meta.source_watermark).getTime();
  if (Number.isNaN(at)) return false;
  return Date.now() - at > STALE_BUDGET_MS;
}

function overflowTotal(packet: ContextPacket): number {
  return packet.overflow.hidden_node_count + packet.overflow.hidden_edge_count;
}

function usePacket(contextId: string, lensId: string | null) {
  const [data, setData] = React.useState<ContextPacket | null>(null);
  const [loading, setLoading] = React.useState(Boolean(lensId));
  const [error, setError] = React.useState<string | null>(null);
  const [reloadKey, setReloadKey] = React.useState(0);
  const reload = React.useCallback(() => setReloadKey((v) => v + 1), []);

  React.useEffect(() => {
    if (!lensId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get<ContextPacket>(`/contexts/${encodeURIComponent(contextId)}/explorer`, {
        query: { lens_id: lensId },
      })
      .then((packet) => {
        if (!cancelled) setData(packet);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 403) {
          setError("forbidden");
          return;
        }
        setError(err instanceof Error ? err.message : "Failed to load context packet.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [contextId, lensId, reloadKey]);

  return { data, loading, error, reload };
}

function PacketContent({ packet }: { packet: ContextPacket }) {
  const stale = isStale(packet);
  const overflow = overflowTotal(packet);
  const redactedCount = packet.redactions.length;

  return (
    <div className="flex flex-col gap-6">
      {/* The three architecture markers, made explicit and never hidden. */}
      {stale ? <StaleBanner asOf={formatRelative(packet.meta.source_watermark)} /> : null}
      {redactedCount > 0 ? (
        <RedactedBanner
          count={redactedCount}
          fields={packet.redactions.map((r) => humanize(r.reason))}
        />
      ) : null}
      {overflow > 0 ? (
        <OverflowBanner
          count={overflow}
          message={`${packet.overflow.hidden_node_count} node(s) and ${packet.overflow.hidden_edge_count} edge(s) hidden past the budget${
            packet.overflow.reason ? ` (${humanize(packet.overflow.reason)})` : ""
          }.`}
        />
      ) : null}

      <InspectorSection title="Packet metadata">
        <FieldGrid>
          <Field label="Lens">
            <span className="font-mono text-xs">
              {packet.meta.lens_id}@{packet.meta.lens_version}
            </span>
          </Field>
          <Field label="Semantic level">{packet.meta.semantic_level}</Field>
          <Field label="Nodes">
            {packet.meta.node_count} / {packet.meta.budget.max_nodes}
          </Field>
          <Field label="Edges">
            {packet.meta.edge_count} / {packet.meta.budget.max_edges}
          </Field>
          <Field label="Generated">{formatDateTime(packet.meta.generated_at)}</Field>
          <Field label="Source watermark">
            {formatDateTime(packet.meta.source_watermark)}
            {stale ? <span className="ml-2 text-warning-foreground">(stale)</span> : null}
          </Field>
          <Field label="Truncated">{packet.meta.truncated ? "Yes" : "No"}</Field>
          <Field label="Root contexts">
            <span className="font-mono text-xs">
              {packet.meta.root_context_ids.join(", ") || "—"}
            </span>
          </Field>
        </FieldGrid>
      </InspectorSection>

      <InspectorSection title="Redactions">
        {redactedCount === 0 ? (
          <p className="text-sm text-muted-foreground">
            No content was withheld from this packet for the current viewer.
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            {packet.redactions.map((r, i) => (
              <div
                key={`${r.scope}-${r.reason}-${i}`}
                data-testid="packet-redaction"
                className="flex flex-wrap items-center gap-2 rounded-md border border-border px-3 py-2 text-sm"
              >
                <Icon name="roles" className="text-muted-foreground" aria-hidden />
                <span className="font-medium">{humanize(r.scope)}</span>
                <Badge variant="outline">{humanize(r.reason)}</Badge>
                <span className="text-muted-foreground">disclosure: {humanize(r.disclosure)}</span>
              </div>
            ))}
          </div>
        )}
      </InspectorSection>

      <InspectorSection title={`Nodes (${packet.nodes.length})`}>
        {packet.nodes.length === 0 ? (
          <p className="text-sm text-muted-foreground">No authorized nodes in this packet.</p>
        ) : (
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            {packet.nodes.map((node) => (
              <Card key={node.id}>
                <CardHeader className="p-3 pb-1">
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="text-sm [overflow-wrap:anywhere]">{node.label}</CardTitle>
                    <StatusBadge status={node.status.kind} />
                  </div>
                </CardHeader>
                <CardContent className="p-3 pt-0">
                  <Badge variant="outline">{humanize(node.kind)}</Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </InspectorSection>
    </div>
  );
}

export function PacketInspectorRoute() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const lensId = searchParams.get("lens_id");

  if (!id) {
    return <RenderState state="error" errorMessage="Missing context id." />;
  }
  return <PacketInspectorPage contextId={id} lensId={lensId} />;
}

function PacketInspectorPage({
  contextId,
  lensId,
}: {
  contextId: string;
  lensId: string | null;
}) {
  const { data, loading, error, reload } = usePacket(contextId, lensId);

  let body: React.ReactNode;
  if (!lensId) {
    body = (
      <RenderState
        state="empty"
        empty={{
          icon: "scope",
          title: "Choose a lens to render this context packet",
          hint: "Append ?lens_id=… to inspect what a specific lens projects, including any redaction, overflow, or staleness.",
        }}
      />
    );
  } else if (loading) {
    body = <RenderState state="loading" loadingRows={6} />;
  } else if (error === "forbidden") {
    body = (
      <RenderState
        state="forbidden"
        forbiddenReason="You are not authorized to view this context packet."
        forbiddenApprovalPath="the context owner or an access grant"
      />
    );
  } else if (error) {
    body = <RenderState state="error" errorMessage={error} onRetry={reload} />;
  } else if (data) {
    body = <PacketContent packet={data} />;
  } else {
    body = (
      <RenderState state="empty" empty={{ icon: "scope", title: "No packet rendered." }} />
    );
  }

  return (
    <div className="max-w-5xl">
      <div className="flex flex-col gap-6">
        <PageHeader
          title="Context Packet Inspector"
          subtitle="What a lens actually projected for the current viewer — including the redaction, overflow, and staleness it had to apply. Read-only."
          icon="scope"
          actions={
            lensId ? (
              <Button variant="outline" size="sm" onClick={reload}>
                <Icon name="reopen" data-icon="inline-start" aria-hidden />
                Refresh
              </Button>
            ) : undefined
          }
        />
        {body}
      </div>
    </div>
  );
}

export default PacketInspectorRoute;
